import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import Subheader from "../../../components/ui/Subheader";
import { Button, Select, Space } from "antd";
import IconButton from "../../../components/common/IconButton";
import UploadIcon from "../../../assets/icons/UploadIcon";
import {
  addKeyInArray,
  ArrayOption,
  selectSearchOption,
} from "../../../helpers/siteConfig";
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from "../../../store/store";
import {
  getAllAreaAsync,
  getAllRegionAsync,
} from "../../../store/features/NP/geoInfo/geoAPI";
import { ColumnsType } from "antd/es/table";
import { countParPageSerialNumber } from "../../../utils/number";
import useTable from "../../../hooks/useTable";
import { formatDate, formattedPrice } from "../../../utils/time";
import CustomTable from "../../../components/common/CustomTable";
import { Link } from "react-router-dom";
import useModal from "../../../hooks/useModal";
import ImportPriceMatrixExcelModal from "./ImportPriceMatrixExcelModal";
import {
  GetALLPriceMartrixParams,
  GetAllPriceMatrixData,
} from "../../../types/redux";
import { getAllPriceMatrixAsync } from "../../../store/features/NP/payment/paymentAPI";
import { capitalize, formatAmount } from "../../../utils/string";
import { toast } from "../../../helpers/toast";
import EyeIcon from "../../../assets/icons/EyeIcon";
import EditIcon from "../../../assets/icons/EditIcon";

export default function PriceMatrixList() {
  const [regionId, setRegionId] = useState<string | undefined>(undefined);
  const [areaId, setAreaId] = useState(undefined);
  const dispatch = useAppDispatch();

  const { page, limit, handlePagination } = useTable();
  const {
    open: priceMatrixOpen,
    toggle: priceMatrixToggle,
    handleOpen: handlePriceMatrixOpen,
  } = useModal();

  const { login } = useAppSelector((state) => state.login);
  const { regions, areas } = useAppSelector((state) => state.npGeo);
  const { allPriceMatrix, loading, allPriceMatrixTotal } = useAppSelector(
    (state: RootState) => state.payment
  );

  useEffect(() => {
    dispatch(getAllRegionAsync({}));
  }, [dispatch]);

  useEffect(() => {
    if (regionId)
      dispatch(
        getAllAreaAsync({
          params: {
            region: regionId,
          },
        })
      );
  }, [dispatch, regionId]);

  const priceMatrixData = useCallback(() => {
    const params: GetALLPriceMartrixParams = {
      cluster: regionId,
      zone: areaId,
      limit: limit,
      skip: limit * (page - 1),
    };

    dispatch(
      getAllPriceMatrixAsync({
        params,
      })
    );
  }, [dispatch, page, limit, regionId, areaId]);

  useEffect(() => {
    priceMatrixData();
  }, [priceMatrixData]);

  const columns: ColumnsType<GetAllPriceMatrixData> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },

    {
      title: "Cluster Name",
      dataIndex: "cluster",
      render: (val) => {
        return (
          <>
            <div>{val?.regionName ? capitalize(val?.regionName) : "N/A"}</div>
          </>
        );
      },
    },

    {
      title: "Zone Name",
      dataIndex: "zone",
      render: (val) => {
        return (
          <>
            <div>{val?.areaName ? capitalize(val?.areaName) : "N/A"}</div>
          </>
        );
      },
    },

    {
      title: "Vendor",
      dataIndex: "vendor",
      render: (val) => {
        return (
          <div>
            {val?.firstName && val?.lastName
              ? `${val.firstName} ${val.lastName}`
              : "N/A"}
          </div>
        );
      },
    },

    {
      title: "Vendor ID",
      dataIndex: "vendorId",
      align: "center",
      render: (val) => {
        return (
          <>
            <div>{val ? val : "N/A"}</div>
          </>
        );
      },
    },

    {
      title: "Effective Date",
      dataIndex: "effectiveDate",
      align: "center",
      render: (val) => {
        return (
          <>
            <div>{val ? formatDate(val) : "N/A"}</div>
          </>
        );
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_Missed Call</span>
          <br />
          <span>Std_Line Item # 1</span>
        </div>
      ),
      align: "center",
      dataIndex: "standardMissedCallLineItem",
      render: (val) => {
        return (
          <>
            <div>{val ? val : "N/A"}</div>
          </>
        );
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_Missed Call</span>
          <br />
          <span>Std_Missed call Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "standardMissedCallAmount",
      render: (val) => {
        return (
          <>
            <div>{val ? formatAmount(val) : "0"}</div>
          </>
        );
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_Valid TT_Single PG</span>
          <br />
          <span>Std_Line Item # 2</span>
        </div>
      ),
      align: "center",
      dataIndex: "standardValidTTSinglePGLineItem",
      render: (val) => {
        return (
          <>
            <div>{val ? val : "N/A"}</div>
          </>
        );
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_Valid TT_Single PG</span>
          <br />
          <span>Std_Valid TT Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "standardValidTTSinglePGAmount",
      render: (val) => {
        return (
          <>
            <div>{val ? formatAmount(val) : "0"}</div>
          </>
        );
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_Valid TT_Double PG</span>
          <br />
          <span>Std_Line Item # 3</span>
        </div>
      ),
      align: "center",
      dataIndex: "standardValidTTDoublePGLineItem",
      render: (val) => {
        return (
          <>
            <div>{val ? val : "-"}</div>
          </>
        );
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_Valid TT_Double PG</span>
          <br />
          <span>Std_Valid TT Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "standardValidTTDoublePGAmount",
      render: (val) => {
        return (
          <>
            <div>{val ? formatAmount(val) : "0"}</div>
          </>
        );
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_PGRH</span>
          <br />
          <span>Std_Line Item # 4</span>
        </div>
      ),
      align: "center",
      dataIndex: "standardPGRHLineItem",
      render: (val) => {
        return (
          <>
            <div>{val ? val : "-"}</div>
          </>
        );
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Standard_PGRH</span>
          <br />
          <span>Std_PGRH Amount</span>
        </div>
      ),
      align: "center",
      dataIndex: "standardPGRHAmount",
      render: (val) => {
        return (
          <>
            <div>{val ? formatAmount(val) : "0"}</div>
          </>
        );
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_Missed Call</span>
          <br />
          <span>Prm_Line Item # 1</span>
        </div>
      ),
      dataIndex: "premiumMissedCallLineItem",
      align: "center",
      render: (val) => {
        return <div>{val ? val : "N/A"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableYellow",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_Missed Call</span>
          <br />
          <span>Prm_Missed call Amount</span>
        </div>
      ),
      dataIndex: "premiumMissedCallAmount",
      align: "center",
      render: (val) => {
        return <div>{val ? formatAmount(val) : "0"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableYellow",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_Valid TT_Single PG</span>
          <br />
          <span>Prm_Line Item # 2</span>
        </div>
      ),
      dataIndex: "premiumValidTTSinglePGLineItem",
      align: "center",
      render: (val) => {
        return <div>{val ? val : "N/A"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableYellow",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_Valid TT_Single PG</span>
          <br />
          <span>Prm_Valid TT Amount</span>
        </div>
      ),
      dataIndex: "premiumValidTTSinglePGAmount",
      align: "center",
      render: (val) => {
        return <div>{val ? formatAmount(val) : "0"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableYellow",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_Valid TT_Double PG</span>
          <br />
          <span>Prm_Line Item # 3</span>
        </div>
      ),
      dataIndex: "premiumValidTTDoublePGLineItem",
      align: "center",
      render: (val) => {
        return <div>{val ? val : "N/A"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableYellow",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_Valid TT_Double PG</span>
          <br />
          <span>Prm_Valid TT Amount</span>
        </div>
      ),
      dataIndex: "premiumValidTTDoublePGAmount",
      align: "center",
      render: (val) => {
        return <div>{val ? formatAmount(val) : "0"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableYellow",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_PGRH</span>
          <br />
          <span>Prm_Line Item # 4</span>
        </div>
      ),
      dataIndex: "premiumPGRHLineItem",
      align: "center",
      render: (val) => {
        return <div>{val ? val : "N/A"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableYellow",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Premium_PGRH</span>
          <br />
          <span>Prm_PGRH Amount</span>
        </div>
      ),
      dataIndex: "premiumPGRHAmount",
      align: "center",
      render: (val) => {
        return <div>{val ? formatAmount(val) : "0"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableYellow",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_Missed Call</span>
          <br />
          <span>Con_Line Item # 1</span>
        </div>
      ),
      dataIndex: "contingencyMissedCallLineItem",
      align: "center",
      render: (val) => {
        return <div>{val ? val : "N/A"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_Missed Call</span>
          <br />
          <span>Con_Missed call Amount</span>
        </div>
      ),
      dataIndex: "contingencyMissedCallAmount",
      align: "center",
      render: (val) => {
        return <div>{val ? formatAmount(val) : "0"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_Valid TT_Single PG</span>
          <br />
          <span>Con_Line Item # 2</span>
        </div>
      ),
      dataIndex: "contingencyValidTTSinglePGLineItem",
      align: "center",
      render: (val) => {
        return <div>{val ? val : "N/A"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_Valid TT_Single PG</span>
          <br />
          <span>Con_Valid TT Amount</span>
        </div>
      ),
      dataIndex: "contingencyValidTTSinglePGAmount",
      align: "center",
      render: (val) => {
        return <div>{val ? formatAmount(val) : "0"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_Valid TT_Double PG</span>
          <br />
          <span>Con_Line Item # 3</span>
        </div>
      ),
      dataIndex: "contingencyValidTTDoublePGLineItem",
      align: "center",
      render: (val) => {
        return <div>{val ? val : "N/A"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_Valid TT_Double PG</span>
          <br />
          <span>Con_Valid TT Amount</span>
        </div>
      ),
      dataIndex: "contingencyValidTTDoublePGAmount",
      align: "center",
      render: (val) => {
        return <div>{val ? formatAmount(val) : "0"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_PGRH</span>
          <br />
          <span>Con_Line Item # 4</span>
        </div>
      ),
      dataIndex: "contingencyPGRHLineItem",
      align: "center",
      render: (val) => {
        return <div>{val ? val : "N/A"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: (
        <div className="items-center justify-center gap-3">
          <span>Contingency_PGRH</span>
          <br />
          <span>Con_PGRH Amount</span>
        </div>
      ),
      dataIndex: "contingencyPGRHAmount",
      align: "center",
      render: (val) => {
        return <div>{val ? formatAmount(val) : "0"}</div>;
      },
      onHeaderCell: () => {
        return {
          className: "custom_edotco_tableSky",
        };
      },
    },

    {
      title: "Action",
      align: "center",
      render: (val) => (
        <Space>
          {login?.role === "edatco" && (
            <>
              <Link to={`/np/price-matrix/details/${val?._id}`}>
                <Button className="flex items-center gap-2 !text-[#33AB29] !border-none !bg-[#33AB291A]">
                  <EyeIcon color="#33AB29" />
                  <span>View</span>
                </Button>
              </Link>

              <Link to={`/np/price-matrix/edit/${val?._id}`}>
                <Button className="flex items-center gap-2 !text-[#33AB29] !border-none !bg-[#33AB291A]">
                  <EditIcon color="#33AB29" />
                  <span>Edit</span>
                </Button>
              </Link>
            </>
          )}

          {login?.role === "scm" && (
            <>
              <Link to={`/scm/price-matrix/details/${val?._id}`}>
                <Button className="flex items-center gap-2 !text-[#33AB29] !border-none !bg-[#33AB291A]">
                  <EyeIcon color="#33AB29" />
                  <span>View</span>
                </Button>
              </Link>

              <Link to={`/scm/price-matrix/edit/${val?._id}`}>
                <Button className="flex items-center gap-2 !text-[#33AB29] !border-none !bg-[#33AB291A]">
                  <EditIcon color="#33AB29" />
                  <span>Edit</span>
                </Button>
              </Link>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {priceMatrixOpen?.type === "IMPORT_PRICE_MATRIX" && (
        <ImportPriceMatrixExcelModal
          open={priceMatrixOpen?.open}
          data={priceMatrixOpen?.data}
          handleClose={priceMatrixToggle}
          allPriceMatrix={allPriceMatrix}
          priceMatrixData={priceMatrixData}
        />
      )}

      <AdminLayout
        title="Price Matrix List"
        headerTitle="Price Matrix List"
        headerChildren={
          <Space wrap>
            {login?.role === "scm" && (
              <Link to={`/scm/price-matrix-logs`}>
                <div className="justify-start text-blue-600 text-sm font-medium cursor-pointer">
                  View Matrix Log
                </div>
              </Link>
            )}

            {login?.role === "edatco" && (
              <Link to={`/np/price-matrix-logs`}>
                <div className="justify-start text-blue-600 text-sm font-medium cursor-pointer">
                  View Matrix Log
                </div>
              </Link>
            )}
          </Space>
        }
      >
        <section className="mt-7">
          <Subheader
            title="Matrix Table"
            subTitle={``}
            subTitleClass="!text-primary"
          >
            <Space size="middle">
              <Select
                value={regionId}
                placeholder="Select cluster"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={ArrayOption(regions, "regionName", "_id", true)}
                onChange={(val) => {
                  setRegionId(val);
                  setAreaId(undefined);
                }}
                className="min-w-[120px]"
                disabled={login?.role === "region" || login?.role === "finance"}
              />
              <Select
                placeholder="Select zone"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={ArrayOption(areas, "areaName", "_id", true)}
                onChange={(val) => {
                  setAreaId(val);
                }}
                value={areaId}
                disabled={!regionId}
                className="min-w-[120px]"
              />

              <IconButton
                type="primary"
                nextIcon={<UploadIcon />}
                onClick={() => {
                  // if (!regionId && !areaId) {
                  //   toast("warning", "Please select cluster and zone");
                  //   return;
                  // }
                  // if (!regionId) {
                  //   toast("warning", "Please select cluster");
                  //   return;
                  // }
                  // if (!areaId) {
                  //   toast("warning", "Please select zone");
                  //   return;
                  // }

                  // If both are selected, open the modal
                  handlePriceMatrixOpen({
                    type: "IMPORT_PRICE_MATRIX",
                    // data: getSingleBatchmentPayment,
                  });
                }}
              >
                Upload Excel
              </IconButton>
            </Space>
          </Subheader>

          <CustomTable
            loading={loading}
            total={allPriceMatrixTotal}
            page={page}
            limit={limit}
            handlePagination={handlePagination}
            columns={columns}
            dataList={addKeyInArray(allPriceMatrix)}
            className="po_manager_table"
            // isPagination={keyword ? false : true}
          />
        </section>
      </AdminLayout>
    </>
  );
}
